/**
 * Summary Form Validation Utility
 * Validates ALL mandatory and conditionally mandatory fields & documents before submitting summary pages
 * for Land Use, Land Use Conversion, Map Submission, and Form Completion applications.
 */

const isEmpty = (val) => {
  if (val === undefined || val === null || val === "") return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;

  if (typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
};

// Normalize a document type key: lowercase, strip dots/underscores/spaces
const normalizeDocKey = (key) =>
  typeof key === "string" ? key.toLowerCase().replace(/[._\s-]/g, "") : "";

const getDocumentTypes = (reduxDocs = [], formDocs = []) => {
  // exact set for backward compat
  const types = new Set();
  // normalized set for MDMS-style keys (e.g. BPA.COMPLETION.REGISTRATION_CERTIFICATE)
  const normalizedTypes = new Set();

  const addKey = (key) => {
    if (key) {
      types.add(key);
      normalizedTypes.add(normalizeDocKey(key));
    }
  };

  const processDoc = (d) => {
    // Accept docs that have a non-empty fileStoreId OR a server-side id/documentUid field
    const hasValidFileStore =
      (d?.fileStoreId && typeof d.fileStoreId === "string" && d.fileStoreId.trim() !== "") ||
      (d?.id && typeof d.id === "string" && d.id.trim() !== "") ||
      (d?.documentUid && typeof d.documentUid === "string" && d.documentUid.trim() !== "");

    if (hasValidFileStore) {
      addKey(d?.documentType);
      addKey(d?.name);
      addKey(d?.additionalDetails?.title);
      // Also add the last segment of MDMS-style dotted keys (e.g. "REGISTRATION_CERTIFICATE")
      if (d?.documentType && d.documentType.includes(".")) {
        addKey(d.documentType.split(".").pop());
      }
    }
  };
  (reduxDocs || []).forEach(processDoc);
  (formDocs || []).forEach(processDoc);

  // Expose the normalized set via a property on the returned Set for use in hasDoc
  types._normalized = normalizedTypes;
  return types;
};

const hasDoc = (docSet, ...names) =>
  names.some(
    (name) =>
      docSet.has(name) ||
      (docSet._normalized && docSet._normalized.has(normalizeDocKey(name)))
  );

export const validateSummaryForm = ({
  formType,
  formData = {},
  reduxDocuments = [],
  declarationChecked = true,
  t = (key) => key,
}) => {
  console.log(formData, "Validation data here");
  const errors = [];
  const uploadedDocs = getDocumentTypes(reduxDocuments, formData?.documents);

  // Normalize formType or applicationType
  const rawType = formType || formData?.applicationType || "";
  const normType = rawType.toUpperCase().replace(/_/g, "");

  // 1. Check Declaration Checkbox if explicitly passed as false
  if (declarationChecked === false) {
    errors.push(t("Please check the declaration checkbox before submitting."));
  }

  // 2. Validate Authority / Office / Unit selection
  const appFlow = formData?.department || formData?.applicationFlow;
  if (appFlow === "DA") {
    if (isEmpty(formData?.selectedOffice) && isEmpty(formData?.verifyingAuthority)) {
      errors.push(t("Verifying Authority / Office selection is required."));
    }
  } else if (appFlow === "AVP") {
    if (
      isEmpty(formData?.selectedUnit) &&
      isEmpty(formData?.selectedDevelopmentAuthority) &&
      isEmpty(formData?.landAreaDetails?.city) &&
      isEmpty(formData?.selectedCity)
    ) {
      errors.push(t("Unit / City selection is required."));
    }
  }

  // 3. Validate Owner Information
  const owner = formData?.landInfo?.owners?.[0] || formData?.owners?.[0];
  // if (!owner) {
  //   errors.push(t("Owner Information is missing."));
  // } else {
  //   if (isEmpty(owner.name)) {
  //     errors.push(t("Owner Name is required in Owner Information."));
  //   }
  //   if (isEmpty(owner.fatherOrHusbandName)) {
  //     errors.push(t("Father/Husband Name is required in Owner Information."));
  //   }
  //   if (isEmpty(owner.mobileNumber)) {
  //     errors.push(t("Mobile Number is required in Owner Information."));
  //   }
  //   if (isEmpty(owner.gender)) {
  //     errors.push(t("Gender is required in Owner Information."));
  //   }

  //   // Correspondence Address
  //   const corr = owner.correspondenceAddress || formData?.correspondenceAddress;
  //   if (!corr || isEmpty(corr.address)) {
  //     errors.push(t("Correspondence Address line is required."));
  //   }
  //   if (!corr || (isEmpty(corr.district) && isEmpty(corr.town) && isEmpty(corr.city) && isEmpty(corr.state))) {
  //     errors.push(t("Correspondence District/State is required."));
  //   }
  //   if (!corr || isEmpty(corr.pinno)) {
  //     errors.push(t("Correspondence Pincode is required."));
  //   }

  //   // Permanent Address (commented as in original)
  //   // const perm = owner.permanentAddress || formData?.permanentAddress;
  //   // if (formData.applicationType !== "MAP_SUB" && !perm || isEmpty(perm.address)) {
  //   //   errors.push(t("Permanent Address line is required."));
  //   // }
  //   // if (!perm || (isEmpty(perm.district) && isEmpty(perm.town) && isEmpty(perm.city) && isEmpty(perm.state))) {
  //   //   errors.push(t("Permanent District/State is required."));
  //   // }
  //   // if (!perm || isEmpty(perm.pinno)) {
  //   //   errors.push(t("Permanent Pincode is required."));
  //   // }
  // }

  // 4. Form-Specific Validation Logic

  // --- LAND USE & LAND USE CONVERSION ---
  if (normType.includes("CONVERSION") || normType.includes("LANDUSE") || normType.includes("LAND")) {
    const prof = formData?.professionalInformation || formData?.professionaldetails;
    if (prof && prof.consultantCategory !== "Owner") {
      if (isEmpty(prof.name)) errors.push(t("LTP Name is required in LTP Information."));
      if (isEmpty(prof.mobileNumber) && isEmpty(prof.mobileNo)) errors.push(t("LTP Mobile Number is required in LTP Information."));
    }

    const land = formData?.descriptionOfLand || {};
    if (isEmpty(land.schemeType)) {
      errors.push(t("Scheme Type (Scheme / Non Scheme) is required in Land Information."));
    } else if (land.schemeType === "Scheme") {
      if (isEmpty(land.subLandUseAsPerLayout) && isEmpty(land.appliedLandUse)) {
        errors.push(t("Sub Land Use / Applied Land Use is required for Scheme type."));
      }
      if (isEmpty(land.schemeName) && isEmpty(land.sectorNo)) {
        errors.push(t("Scheme Name / Sector is required for Scheme type."));
      }
    } else if (land.schemeType === "Non Scheme") {
      if (isEmpty(land.appliedLandUse)) {
        errors.push(t("Applied Land Use is required for Non Scheme type."));
      }
      // if (isEmpty(land.revenueVillageMohal)) {
      //   errors.push(t("Revenue Village / Mohal is required for Non Scheme type."));
      // }
      const hasKhata = !isEmpty(land.khataNo) || !isEmpty(land.khatauniNo);
      const hasKhasraArray = Array.isArray(land.multipleKhasra) && land.multipleKhasra.some((k) => !isEmpty(k.khasraNo));
      // if (!hasKhata && !hasKhasraArray) {
      //   errors.push(t("Khata / Khatauni / Khasra Number details are required for Non Scheme type."));
      // }
    }

    // Mandatory Documents check for Land Use & Land Use Conversion
    const isScheme = land.schemeType === "Scheme";
    if (!hasDoc(uploadedDocs, "completeDrawingFile", "completeDrawing")) {
      errors.push(t("Complete Drawing File is required in Documents tab."));
    }
    if (!hasDoc(uploadedDocs, "registrySaleDeedHPTA", "registrySaleDeed", "Registry/Sale deed/HPTA")) {
      errors.push(t("Registry / Sale Deed / HPTA Document is required in Documents tab."));
    }
    if (isScheme && !hasDoc(uploadedDocs, "allotmentLetterPossessionLetter", "allotmentLetter", "possessionLetter")) {
      errors.push(t("Allotment Letter / Possession Letter is required for Scheme type in Documents tab."));
    }
  }

  // --- MAP SUBMISSION & GROUP HOUSING ---
  if (normType.includes("MAPSUBMISSION") || normType.includes("MAP") || normType.includes("GROUPHOUSING")) {
    const nature = formData?.natureOfConstruction || formData?.mapSubDetails?.natureOfConstruction;
    if (isEmpty(nature)) {
      errors.push(t("Nature of Construction is required in General Information."));
    }

    // LTP / Professional details check
    const prof = formData?.professionalInformation || formData?.professionaldetails;
    if (formData.applicationType === "FORM_LANDUSE" && prof && prof.consultantCategory !== "Owner") {
      if (isEmpty(prof.name)) errors.push(t("Architect / Engineer Name is required in Professional Details."));
      if (isEmpty(prof.licenseNumber)) errors.push("License Number Cannot be empty")
      if (isEmpty(prof.mobileNumber) && isEmpty(prof.mobileNo)) errors.push(t("Architect / Engineer Mobile Number is required in Professional Details."));
    }

    // Plot / Land details check
    const land = formData?.descriptionOfLand || {};
    const plotNo = formData?.plotNo;
    const khasraNo = formData?.khasraNo || land?.khasraNo || (Array.isArray(land?.multipleKhasra) && land?.multipleKhasra?.[0]?.khasraNo);
    const plotArea = formData?.plotArea || formData?.totalPlotArea;

    if (isEmpty(plotNo) && land?.schemeType === "Scheme") {
      errors.push(t("Plot Number/Khasra Number is required in Plot Details."));
    }
    if (isEmpty(plotArea) || Number(plotArea) <= 0) {
      errors.push(t("Plot Area is required and must be greater than 0 in Plot Details."));
    }

    // ---------- NEW VALIDATIONS ----------
    // 1. Total Built-Up Area (mandatory for all Map Submission forms)
    const totalBuiltUp = formData?.totalBuildUpArea || formData?.mapSubDetails?.totalBuildUpArea;
    if (isEmpty(totalBuiltUp) || Number(totalBuiltUp) <= 0) {
      errors.push(t("Total Built-Up Area is required and must be greater than 0."));
    }
    const isDSCMissing =
      !formData?.IsSignedDrawingPdfURL &&
      !formData?.IsSignedDrawingPdfDscEmp;

    if (isDSCMissing) {
      errors.push(t("DSC is required"));
    }

    // 2. Compounding Checklist (mandatory only when nature is "Compounding")
    if (nature === "Compounding") {
      const compoundingChecklist = formData?.compoundingChecklist || formData?.mapSubDetails?.compoundingChecklist;
      if (isEmpty(compoundingChecklist)) {
        errors.push(t("Compounding Checklist is required when Nature of Construction is Compounding."));
      }
    }
    // ------------------------------------

    // Drawing PDF / EDCR file check
    // If edcrNumber is present, the drawing was auto-loaded from EDCR - no manual upload needed
    const hasEdcrNumber = !isEmpty(formData?.edcrNumber);
    const hasDxfFile = !isEmpty(formData?.dxfFile) || !isEmpty(formData?.updatedDxfFile) || hasDoc(uploadedDocs, "dxfDrawing");
    const hasDrawingPdf = hasDoc(uploadedDocs, "drawingPdf", "drawingPDF", "drawingDetail", "compoundingDrawingPdf");
    const hasDrawing = hasEdcrNumber || hasDxfFile || hasDrawingPdf || hasDoc(uploadedDocs, "completeDrawingFile", "buildingPlan");
    if (!hasDrawing) {
      errors.push(t("Drawing PDF / DXF / EDCR Scrutiny file is required in Drawing Details / Documents."));
    }

    // Conditional Nature of Construction checks (unchanged)
    const isRevalidationOrRedev =
      nature === "Revalidation/Time Extension" ||
      nature === "Redevelopment/Reconstruction" ||
      (typeof nature === "string" && nature.includes("Redevelopment")) ||
      (typeof nature === "string" && nature.includes("Reconstruction"));

    if (isRevalidationOrRedev) {
      const sanctionDate = formData?.sanctionValidityStartDate || formData?.mapSubDetails?.sanctionValidityStartDate;
      const validityDate = formData?.sanctionValidityDate || formData?.mapSubDetails?.sanctionValidityDate;
      const prevFileNo = formData?.prevFileNo || formData?.mapSubDetails?.prevFileNo;

      if (isEmpty(sanctionDate)) {
        errors.push(t("Previous Sanction Date is required for " + nature + "."));
      }
      if (isEmpty(validityDate)) {
        errors.push(t("Previous Sanction Validity Date is required for " + nature + "."));
      }
      if (isEmpty(prevFileNo)) {
        errors.push(t("Previous Approved Map Number / File Number is required for " + nature + "."));
      }
    }

    // Mandatory Documents check for Map Submission (Step 2 Documents)
    const isDA = formData?.department === "DA";
    const isScheme = land.schemeType === "Scheme" || formData?.schemeType === "Scheme";
    const isSelfCert =
      formData?.planOn === "Self Approval via LTP of Residential Plot up to 500 sqm and Commercial plot up to 200 sqm" ||
      formData?.planOn === "Self Certification of residential Plot Area upto 300 SqM on approved layouts";
    const isLowRiskCase = formData?.riskType === "LOW";
    const scrutinyFlow = formData?.scrutinyFlow === "PDF" || Digit?.SessionStorage?.get("LTP.SCRUTINY.SELECTION") === "PDF";

    if (!hasDoc(uploadedDocs, "Registration Certificate", "registrationCertificate")) {
      errors.push(t("Architect/Engineer Registration Certificate is required in Documents tab."));
    }
    if (!hasDoc(uploadedDocs, "registrySaleDeedHPTA", "registrySaleDeed")) {
      errors.push(t("Registry / Sale Deed / HPTA Document is required in Documents tab."));
    }
    if (!isLowRiskCase && !hasDoc(uploadedDocs, "calculationSheet")) {
      errors.push(t("Calculation sheet of Estimate with Affidavit is required in Documents tab."));
    }
    if (!isScheme && !hasDoc(uploadedDocs, "khatauni")) {
      errors.push(t("Khatauni document is required for Non-Scheme type in Documents tab."));
    }
    if (isScheme && !hasDoc(uploadedDocs, "allotmentLetterPossessionLetter", "allotmentLetter")) {
      errors.push(t("Allotment Letter / Possession Letter is required for Scheme type in Documents tab."));
    }
    if (!isSelfCert && !hasDoc(uploadedDocs, "ownerAffidavit")) {
      errors.push(t("Owner Affidavit is required in Documents tab."));
    }
    if (isSelfCert && !hasDoc(uploadedDocs, "ltpAffidavit")) {
      errors.push(t("LTP Affidavit is required for Self Certification in Documents tab."));
    }
    if (!isDA && !hasDoc(uploadedDocs, "uptodatePaymentLetter")) {
      errors.push(t("Upto date payment letter is required in Documents tab."));
    }
    if (scrutinyFlow && nature !== "Compounding" && !hasDoc(uploadedDocs, "dxfDrawing")) {
      errors.push(t("DXF Drawing file is required for PDF scrutiny flow in Documents tab."));
    }

    // Conditional Sanction Documents check
    const showSanctionDocs =
      nature !== "New" &&
      (nature !== "Compounding" || formData?.serviceType === "yes" || formData?.shortName === "yes");
    const isSanctionDocMandatory =
      nature !== "Redevelopment/Reconstruction" && nature !== "Redevelopment";

    if (showSanctionDocs && isSanctionDocMandatory) {
      if (!hasDoc(uploadedDocs, "Previous Sanction Map", "previousSanctionMap")) {
        errors.push(t("Previous Sanction Map document is required in Documents tab."));
      }
      if (!hasDoc(uploadedDocs, "Previous Sanction Letter", "previousSanctionLetter")) {
        errors.push(t("Previous Sanction Letter document is required in Documents tab."));
      }
    }
  }

  // --- FORM COMPLETION ---
  if (normType.includes("COMPLETION")) {
    const nature = formData?.natureOfConstruction || formData?.mapSubDetails?.natureOfConstruction;
    const isRevalidationOrRedev =
      nature === "Revalidation/Time Extension" ||
      nature === "Redevelopment/Reconstruction" ||
      (typeof nature === "string" && nature.includes("Redevelopment")) ||
      (typeof nature === "string" && nature.includes("Reconstruction"));

    if (isRevalidationOrRedev) {
      const sanctionDate = formData?.sanctionValidityStartDate || formData?.mapSubDetails?.sanctionValidityStartDate;
      const validityDate = formData?.sanctionValidityDate || formData?.mapSubDetails?.sanctionValidityDate;
      const prevFileNo = formData?.prevFileNo || formData?.mapSubDetails?.prevFileNo;

      if (isEmpty(sanctionDate)) {
        errors.push(t("Previous Sanction Date is required for " + nature + "."));
      }
      if (isEmpty(validityDate)) {
        errors.push(t("Previous Sanction Validity Date is required for " + nature + "."));
      }
      if (isEmpty(prevFileNo)) {
        errors.push(t("Previous Approved Map Number is required for " + nature + "."));
      }
    }

    // Completion Documents Check
    const isLowRiskCase = formData?.riskType === "LOW";
    const isSelfCert =
      formData?.planOn === "Self Approval via LTP of Residential Plot up to 500 sqm and Commercial plot up to 200 sqm";

    // Pre-compute booleans with broad key aliases (MDMS-style, camelCase, and human-readable variants)
    const hasRegCert = hasDoc(
      uploadedDocs,
      "Registration Certificate", "registrationCertificate",
      "REGISTRATION_CERTIFICATE",
      "BPA.COMPLETION.REGISTRATION_CERTIFICATE",
      "BPA.LANDUSE.REGISTRATION_CERTIFICATE",
      "BPA.MAPSUB.REGISTRATION_CERTIFICATE"
    );
    const hasRegSaleDeed = hasDoc(
      uploadedDocs,
      "registrySaleDeedHPTA", "registrySaleDeed", "Registry/Sale deed/HPTA",
      "REGISTRY_SALE_DEED_HPTA", "REGISTRY_SALE_DEED"
    );
    const hasCalcSheet = hasDoc(uploadedDocs, "calculationSheet", "CALCULATION_SHEET");
    const hasKhatauni = hasDoc(uploadedDocs, "khatauni", "KHATAUNI");
    const hasAllotmentLetter = hasDoc(
      uploadedDocs,
      "allotmentLetterPossessionLetter", "allotmentLetter", "possessionLetter",
      "ALLOTMENT_LETTER", "POSSESSION_LETTER"
    );
    const hasOwnerAffidavit = hasDoc(uploadedDocs, "ownerAffidavit", "OWNER_AFFIDAVIT");
    const hasLtpAffidavit = hasDoc(uploadedDocs, "ltpAffidavit", "LTP_AFFIDAVIT");
    const hasDrawingPdf = hasDoc(uploadedDocs, "drawingPDF", "drawingPdf", "DRAWING_PDF");

    // Scheme detection: check multiple data locations since schemeType may be nested.
    // Also, if the allotment letter doc is already uploaded, it implies a Scheme-type application
    // (allotmentLetter and khatauni are mutually exclusive — scheme vs. non-scheme).
    const schemeTypeValue =
      formData?.descriptionOfLand?.schemeType ||
      formData?.schemeType ||
      formData?.mapSubDetails?.schemeType ||
      formData?.additionalDetails?.schemeType ||
      formData?.additionalDetails?.descriptionOfLand?.schemeType;

    // isScheme: checked via schemeTypeValue (multi-location) OR inferred from allotment letter presence.
    // allotmentLetter and khatauni are mutually exclusive (scheme vs. non-scheme), so if allotment
    // letter is already uploaded we know it's a scheme case and skip the khatauni requirement.
    const isScheme = schemeTypeValue === "Scheme" || hasAllotmentLetter;

    if (!hasRegCert) {
      errors.push(t("Architect/Engineer Registration Certificate is required in Documents tab."));
    }
    if (!hasRegSaleDeed) {
      errors.push(t("Registry / Sale Deed / HPTA document is required in Documents tab."));
    }
    if (!isLowRiskCase && !hasCalcSheet) {
      errors.push(t("Calculation sheet of Estimate with Affidavit is required in Documents tab."));
    }
    // Khatauni is only required for non-scheme applications.
    // If allotment letter is uploaded, it's a scheme — skip khatauni check.
    if (!isScheme && !hasKhatauni) {
      errors.push(t("Khatauni document is required in Documents tab."));
    }
    // Allotment letter required for scheme type only (and only if schemeType is explicitly set).
    // If we only inferred scheme from allotment letter presence, we already have it — skip.
    if (schemeTypeValue === "Scheme" && !hasAllotmentLetter) {
      errors.push(t("Allotment Letter / Possession Letter is required in Documents tab."));
    }
    if (!isSelfCert && !hasOwnerAffidavit) {
      errors.push(t("Owner Affidavit is required in Documents tab."));
    }
    if (isSelfCert) {
      if (!hasDrawingPdf) {
        errors.push(t("Drawing PDF is required for Self Certification."));
      }
      if (!hasLtpAffidavit) {
        errors.push(t("LTP Affidavit is required for Self Certification."));
      }
    }
    if (nature === "Compounding") {
      if (!hasDoc(uploadedDocs, "compoundingDrawingPdf", "COMPOUNDING_DRAWING_PDF")) {
        errors.push(t("Compounding Drawing PDF is required."));
      }
      if (!hasDoc(uploadedDocs, "compoundingDrawingDxf", "COMPOUNDING_DRAWING_DXF")) {
        errors.push(t("Compounding Drawing DXF is required."));
      }
      if (!hasDoc(uploadedDocs, "uptodatePaymentLetter", "UPTODATE_PAYMENT_LETTER")) {
        errors.push(t("Site Photo document is required."));
      }
    }
  }

  const isValid = errors.length === 0;
  const errorHtml = isValid
    ? ""
    : `<ul style="text-align: left; margin-top: 10px; padding-left: 20px; font-size: 14px; line-height: 1.6; color: #721c24;">` +
    errors.map((err) => `<li>${err}</li>`).join("") +
    `</ul>`;

  return { isValid, errors, errorHtml };
};